import { faChevronDown, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { getFetchOptions } from "utils/fetch";
import AiChatIcon from "./AiChatIcon";
import {
	ChatStatus,
	StreamFinishReason,
	ChatEventKind,
	ChatPhase,
	PanelStateChange,
	InteractionType
} from "./AiChatConstants";
import "./AiChatTranscriptPanel.scss";

const COLLAPSED_HEIGHT = 200;

function newId() {
	if (window.crypto && typeof window.crypto.randomUUID === "function") {
		return window.crypto.randomUUID();
	} else {
		const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
		return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
	}
}

function findLastIndexByRole(items, role) {
	for (let i = items.length - 1; i >= 0; i--) {
		if (items[i].role === role) {
			return i;
		}
	}

	return -1;
}

function findLastByRole(items, role) {
	for (let i = items.length - 1; i >= 0; i--) {
		if (items[i].role === role) {
			return items[i];
		}
	}

	return null;
}

function ChatBubble({ role, html, text, children }) {
	const isUser = role === "user";
	const bubbleClass = isUser ? "ai-chat__bubble ai-chat__bubble--user" : "ai-chat__bubble ai-chat__bubble--assistant";

	return (
		<div className={bubbleClass}>
			<div className={isUser ? "ai-chat__bubble__icon" : "ai-chat__assistant-icon"}>
				{isUser ? <FontAwesomeIcon icon={faUser} /> : <AiChatIcon className="ai-chat__bubble__icon-svg" />}
			</div>

			<div className="ai-chat__bubble__content">
				{role === "assistant" ? (
					typeof html === "string" && html.trim().length > 0 ? (
						<div className="ai-chat__bubble__content__text" dangerouslySetInnerHTML={{ __html: html }} />
					) : (
						<span>{text ?? ""}</span>
					)
				) : (
					<span>{text ?? ""}</span>
				)}
				{children}
			</div>
		</div>
	);
}

/**
 * Props:
 * - enabled: whether the AI panel should exist at all
 * - initialQuestion: the primary search term to start a new conversation
 */
const AiChatTranscriptPanel = ({ enabled, initialQuestion, }) => {
	const [conversationId, setConversationId] = useState(null);
	const [messages, setMessages] = useState([]);
	const [secondaryInput, setSecondaryInput] = useState("");
	const [status, setStatus] = useState(ChatStatus.Idle);
	const [error, setError] = useState("");
	const [currentPhase, setCurrentPhase] = useState("");

	const [hasNoAnswer, setHasNoAnswer] = useState(false);
	const [isForbidden, setIsForbidden] = useState(false);
	const [isRateLimited, setIsRateLimited] = useState(false);

	const [isExpanded, setIsExpanded] = useState(false);
	const [hasExpanded, setHasExpanded] = useState(false);
	const [contentHeight, setContentHeight] = useState(0);

	const abortControllerRef = useRef(null);
	const formRef = useRef(null);
	const panelRef = useRef(null);
	const contentRef = useRef(null);
	const statusRef = useRef(status);
	const lastStreamedQuestionRef = useRef(null);

	useEffect(() => {
		statusRef.current = status;
	}, [status]);

	const hasAnyAssistantResponse = useMemo(() => {
		return messages.some((m) => {
			return m.role === "assistant" && typeof m.html === "string" && m.html.trim().length > 0;
		});
	}, [messages]);

	const latestAssistantMessage = findLastByRole(messages, "assistant");

	const latestAssistantHasStarted =
		latestAssistantMessage !== null &&
		typeof latestAssistantMessage.html === "string" &&
		latestAssistantMessage.html.trim().length > 0;

	const isShowingPhasePlaceholder = !!currentPhase && !latestAssistantHasStarted;
	const hasFinishedStreaming =
		status === ChatStatus.Done ||
		status === ChatStatus.Error ||
		status === ChatStatus.Aborted;

	const visibleMessages = useMemo(() => {
		return messages.filter((m) => {
			if (m.role !== "assistant") {
				return true;
			}

			const hasHtml = typeof m.html === "string" && m.html.trim().length > 0;
			const hasText = typeof m.text === "string" && m.text.trim().length > 0;

			return hasHtml || hasText;
		});
	}, [messages]);

	const lastVisibleUserIndex = findLastIndexByRole(visibleMessages, "user");
	const lastAssistantIndex = findLastIndexByRole(visibleMessages, "assistant");

	useEffect(() => {
		if (hasExpanded || !isExpanded || isRateLimited || !conversationId) {
			return;
		}

		setHasExpanded(true);

		const options = getFetchOptions({
			method: "POST",
			body: {
				ConversationId: conversationId,
				InteractionType: InteractionType.ShowMoreButtonClicked,
				Value: "true"
			}
		});

		fetch("/api/v3/chat/stream/log-event", options);
	}, [hasExpanded, isExpanded, isRateLimited, conversationId]);

	useEffect(() => {
		if (contentRef.current) {
			setContentHeight(contentRef.current.scrollHeight);
		}
	}, [messages, status, currentPhase]);

	const needsExpander = contentHeight > COLLAPSED_HEIGHT;

	const stop = () => {
		if (abortControllerRef.current !== null) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
			setStatus(ChatStatus.Aborted);
		}
	};

	useEffect(() => {
		const handlePageHide = () => {
			stop();
		};

		window.addEventListener("pagehide", handlePageHide);

		return () => {
			window.removeEventListener("pagehide", handlePageHide);
			handlePageHide();
		};
	}, []);

	const hardResetConversation = () => {
		stop();
		setConversationId(null);
		setMessages([]);
		setSecondaryInput("");
		setStatus(ChatStatus.Idle);
		setError("");
		setHasNoAnswer(false);
		setIsForbidden(false);
		setIsRateLimited(false);
		setIsExpanded(false);
		setHasExpanded(false);
		setContentHeight(0);
		setCurrentPhase("");
	};

	const appendUserMessage = (text) => {
		const id = newId();
		setMessages((prev) => [...prev, { id, role: "user", text, html: null }]);
	};

	const createAssistantPlaceholder = () => {
		const id = newId();
		setMessages((prev) => [...prev, { id, role: "assistant", text: "", html: "" }]);
		return id;
	};

	const appendAssistantHtml = (assistantMessageId, htmlChunk) => {
		setMessages((prev) =>
			prev.map((m) => {
				if (m.id !== assistantMessageId) {
					return m;
				}

				const nextHtml = (m.html ?? "") + (htmlChunk ?? "");
				return { ...m, html: nextHtml };
			})
		);
	};

	useEffect(() => {


		if (!enabled || isForbidden || hasNoAnswer) {
			return;
		}



		if (status === ChatStatus.Streaming) {
			return;
		}

		if (status === ChatStatus.Done) {
			if (!hasAnyAssistantResponse && !error) {
				return;
			}

			setCurrentPhase("");
			return;
		}

		if (status === ChatStatus.Error) {
			setCurrentPhase("");
			return;
		}
	}, [enabled, status, isForbidden, hasNoAnswer, hasAnyAssistantResponse, error, initialQuestion]);

	function getPhaseText(phase) {
		switch (phase) {
			case ChatPhase.Analysing:
				return "Analysing your search...";
			case ChatPhase.Retrieving:
				return "Finding relevant content...";
			case ChatPhase.Generating:
				return "Preparing summary...";
			default:
				return "";
		}
	}

	const initialiseStreamState = () => {
		if (abortControllerRef.current !== null) {
			stop();
		}

		setStatus(ChatStatus.Idle);
		setError("");
		setHasNoAnswer(false);
		setIsForbidden(false);
		setIsRateLimited(false);
		setCurrentPhase(getPhaseText(ChatPhase.Analysing));
	};

	const clearActiveStream = () => {
		abortControllerRef.current = null;
	};

	const applyFinishState = (reason, data = {}) => {
		clearActiveStream();

		switch (reason) {
			case StreamFinishReason.NoAnswer:
				setMessages([]);
				setHasNoAnswer(true);
				setStatus(ChatStatus.Done);
				setCurrentPhase("");
				break;

			case StreamFinishReason.RateLimited:
				setIsRateLimited(true);
				setStatus(ChatStatus.Done);
				setCurrentPhase("");
				break;

			case StreamFinishReason.Forbidden:
				setIsForbidden(true);
				setStatus(ChatStatus.Done);
				setCurrentPhase("");
				break;

			case StreamFinishReason.Error:
				setError(data.message ?? "Unknown error.");
				setStatus(ChatStatus.Error);
				setCurrentPhase("");
				break;

			case StreamFinishReason.Done:
				setStatus(ChatStatus.Done);
				setCurrentPhase("");
				break;

			case StreamFinishReason.Aborted:
				setStatus(ChatStatus.Aborted);
				setCurrentPhase("");
				break;

			default:
				setStatus(reason);
				setCurrentPhase("");
				break;
		}
	};

	const cancelReaderSafely = async (reader) => {
		try {
			await reader.cancel();
		} catch {
			// ignore
		}
	};

	const finishStream = async (reader, reason, data = {}) => {
		applyFinishState(reason, data);
		await cancelReaderSafely(reader);
	};

	const createStreamRequestOptions = ({ conversationId: convId, message, signal }) => {
		const clientMessageId = newId();

		const baseOptions = getFetchOptions({
			method: "POST",
			body: {
				conversationId: convId,
				message,
				clientMessageId
			}
		});

		return {
			...baseOptions,
			signal
		};
	};

	const handleInitialStreamResponse = (resp) => {
		if (resp.status === 409) {
			applyFinishState(StreamFinishReason.Error, {
				message: "Duplicate message detected (already sent)."
			});
			return { shouldContinue: false };
		}

		if (resp.status === 403) {
			applyFinishState(StreamFinishReason.Forbidden);
			return { shouldContinue: false };
		}

		if (!resp.ok || !resp.body) {
			applyFinishState(StreamFinishReason.Error, {
				message: "Request failed."
			});
			return { shouldContinue: false };
		}

		return {
			shouldContinue: true,
			reader: resp.body.getReader(),
			assistantId: createAssistantPlaceholder()
		};
	};

	const processChatEvent = async ({ chatEvent, assistantId, reader }) => {
		const kind = chatEvent.Kind ?? chatEvent.kind;

		switch (kind) {
			case ChatEventKind.ConversationInfo:
			case ChatEventKind.ConversationStarted:
				return { shouldContinue: true };

			case ChatEventKind.RunStarted:
				setCurrentPhase(getPhaseText(ChatPhase.Analysing));
				return { shouldContinue: true };

			case ChatEventKind.PhaseChanged:
				setCurrentPhase(getPhaseText(chatEvent.Phase ?? chatEvent.phase));
				return { shouldContinue: true };

			case ChatEventKind.NoAnswer:
				await finishStream(reader, StreamFinishReason.NoAnswer);
				return { shouldContinue: false };

			case ChatEventKind.RateLimitExceeded:
				await finishStream(reader, StreamFinishReason.RateLimited);
				return { shouldContinue: false };

			case ChatEventKind.HtmlChunk: {
				setCurrentPhase("");

				if (statusRef.current !== ChatStatus.Streaming) {
					setStatus(ChatStatus.Streaming);
				}

				const html = chatEvent.html ?? chatEvent.Html ?? "";

				if (typeof html === "string" && html.length > 0) {
					appendAssistantHtml(assistantId, html);
				}

				return { shouldContinue: true };
			}

			case ChatEventKind.Error:
				await finishStream(reader, StreamFinishReason.Error, {
					message: chatEvent.message ?? chatEvent.Message ?? "Unknown error."
				});
				return { shouldContinue: false };

			case ChatEventKind.Done:
				await finishStream(reader, StreamFinishReason.Done);
				return { shouldContinue: false };

			default:
				return { shouldContinue: true };
		}
	};

	const processNdjsonLine = async ({ line, assistantId, reader }) => {
		const trimmedLine = line.trim();

		if (!trimmedLine) {
			return { shouldContinue: true };
		}

		let chatEvent;

		try {
			chatEvent = JSON.parse(trimmedLine);
		}
		catch {
			await finishStream(reader, StreamFinishReason.Error, {
				message: "Failed to parse NDJSON event."
			});
			return { shouldContinue: false };
		}

		return await processChatEvent({
			chatEvent,
			assistantId,
			reader
		});
	};

	const consumeNdjsonStream = async ({ reader, assistantId }) => {
		const decoder = new TextDecoder("utf-8");
		let buffer = "";

		while (true) {
			const { value, done } = await reader.read();

			if (done) {
				break;
			}

			buffer += decoder.decode(value, { stream: true });

			const lines = buffer.split("\n");
			buffer = lines.pop() ?? "";

			for (const line of lines) {
				const result = await processNdjsonLine({
					line,
					assistantId,
					reader
				});

				if (!result.shouldContinue) {
					return;
				}
			}
		}

		const trailingLine = buffer.trim();

		if (trailingLine) {
			try {
				const result = await processNdjsonLine({
					line: trailingLine,
					assistantId,
					reader
				});

				if (!result.shouldContinue) {
					return;
				}
			}
			catch {
				// ignore trailing parse issue
			}
		}

		applyFinishState(StreamFinishReason.Done);
	};

	const handleTopLevelStreamError = (err) => {
		if (err && err.name === "AbortError") {
			applyFinishState(StreamFinishReason.Aborted);
		} else {
			applyFinishState(StreamFinishReason.Error, {
				message: "Request failed."
			});
		}
	};

	const startStream = async ({ conversationId: convId, message }) => {
		initialiseStreamState();

		const ac = new AbortController();
		abortControllerRef.current = ac;

		const options = createStreamRequestOptions({
			conversationId: convId,
			message,
			signal: ac.signal
		});

		try {
			const resp = await fetch("/api/v3/chat/stream", options);

			const responseResult = handleInitialStreamResponse(resp);

			if (!responseResult.shouldContinue) {
				return;
			}

			await consumeNdjsonStream({
				reader: responseResult.reader,
				assistantId: responseResult.assistantId
			});
		}
		catch (err) {
			handleTopLevelStreamError(err);
		}
	};

	useEffect(() => {
		if (!enabled) {
			hardResetConversation();
			return;
		}

		const trimmed = (initialQuestion ?? "").toString().trim();

		if (!trimmed) {
			hardResetConversation();
			return;
		}

		// Same question re-rendered (e.g. parent re-render with same props) - do not restart.
		if (lastStreamedQuestionRef.current === trimmed) {
			return;
		}

		// Do not interrupt an in-progress stream.
		if (status === ChatStatus.Streaming) {
			return;
		}

		lastStreamedQuestionRef.current = trimmed;

		hardResetConversation();

		const convId = newId();
		setConversationId(convId);

		appendUserMessage(trimmed);
		startStream({ conversationId: convId, message: trimmed });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, initialQuestion]);

	const handleSecondarySubmit = async (e) => {
		e.preventDefault();

		if (!enabled) {
			return;
		}

		if (!conversationId) {
			return;
		}

		const trimmed = (secondaryInput ?? "").toString().trim();
		if (!trimmed) {
			return;
		}

		appendUserMessage(trimmed);
		setSecondaryInput("");

		await startStream({ conversationId, message: trimmed });
	};

	const handleTextareaKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			formRef.current?.requestSubmit();
		}
	};

	const handleToggle = () => {
		const willCollapse = isExpanded;
		setIsExpanded((prev) => !prev);

		if (willCollapse && panelRef.current) {
			panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	if (!enabled) {
		return null;
	}

	if (isForbidden) {
		return null;
	}

	if (hasNoAnswer) {
		return null;
	}

	const isDoneOrIdle =
		status === ChatStatus.Idle ||
		status === ChatStatus.Done ||
		status === ChatStatus.Aborted;

	const hasVisibleContent = visibleMessages.length > 0 || isShowingPhasePlaceholder || !!error;

	if (isDoneOrIdle && !hasVisibleContent) {
		return null;
	}

	const isBusy = status === ChatStatus.Streaming;

	const secondaryForm = hasAnyAssistantResponse && !isRateLimited && hasFinishedStreaming ? (
		<form className="ai-chat__secondary" ref={formRef} onSubmit={handleSecondarySubmit}>
			<p><strong>Is there anything else I can help with?</strong></p>
			<textarea
				name="followUpMessage"
				value={secondaryInput}
				onChange={(e) => setSecondaryInput(e.target.value)}
				onKeyDown={handleTextareaKeyDown}
				placeholder="Continue the conversation..."
				className="ai-chat__textarea"
				rows={2}
				disabled={isBusy}
			/>
			<div className="ai-chat__actions">
				<button type="submit" className="ai-chat__button ai-chat__button--primary" disabled={isBusy}>
					Send
				</button>
			</div>
		</form>
	) : null;

	const bodyStyle = {
		maxHeight: isExpanded ? `${contentHeight}px` : `${COLLAPSED_HEIGHT}px`
	};

	const bodyClass =
		needsExpander && !isExpanded && !isShowingPhasePlaceholder
			? "ai-chat__body ai-chat__body--collapsed"
			: "ai-chat__body";

	return (
		<div className="ai-chat" ref={panelRef}>
			<h2 className="ai-chat__header">AI Overview</h2>

			<div className={bodyClass} style={bodyStyle} ref={contentRef}>
				<div className="ai-chat__transcript">
					{visibleMessages.length === 0 && !isShowingPhasePlaceholder ? (
						<div className="ai-chat__empty">Start a new conversation to see replies here.</div>
					) : (
						<>
							{visibleMessages.map((m, i) => (
								<div key={m.id}>
									<ChatBubble role={m.role} html={m.html} text={m.text}>
										{i === lastAssistantIndex ? secondaryForm : null}
									</ChatBubble>

									{isShowingPhasePlaceholder && i === lastVisibleUserIndex ? (
										<div className="ai-chat__phase-status">
											<div
												className={
													isShowingPhasePlaceholder
														? "ai-chat__assistant-icon ai-chat__assistant-icon--loading"
														: "ai-chat__assistant-icon"
												}
											>
												<AiChatIcon className="ai-chat__bubble__icon-svg" />
											</div>

											<div className="ai-chat__bubble__content">
												<div
													key={currentPhase}
													className="ai-chat__phase-status__text ai-chat__phase-status__text--shimmer"
												>
													<p>{currentPhase}</p>
												</div>
											</div>
										</div>
									) : null}
								</div>
							))}
						</>
					)}
				</div>

				{error ? (
					<div className="ai-chat__error">
						<strong>Error:</strong> {error}
					</div>
				) : null}
			</div>

			{needsExpander && !isShowingPhasePlaceholder ? (
				<button type="button" className="ai-chat__toggle" onClick={handleToggle}>
					{isExpanded ? "Show less" : "Show more"}
					<FontAwesomeIcon
						icon={faChevronDown}
						className={isExpanded ? "ai-chat__chev ai-chat__chev--up" : "ai-chat__chev"}
					/>
				</button>
			) : null}
		</div>
	);
};

export default AiChatTranscriptPanel;